/**
 * VRU 责任碰撞评估器失败场景「相对速度分档饼图」。
 * 「不通过统计」页专用（`?mode=stats`），嵌在评测器聚合明细与任务明细之间。
 *
 * 5 桶固定顺序 0-5 / 5-10 / 10-15 / 15-20 / 20+ (m/s),按碰撞严重性递增取
 * 冷 → 暖色阶(绿 → 黄 → 橙 → 红-橙 → 红),视觉即传递「相对速度越高越危险」;
 * 右侧 KPI 三格:样本数 / 均值 / 最大值,回填后端汇总。
 */
import { useEffect, useMemo, useState } from 'react';
import { Empty, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';

import { getVruRelativeSpeedDist, type VruSpeedDistResp } from '@/apis/simulation';

const { Text } = Typography;

// 5 档冷→暖色阶:绿(0-5,微碰)→ 黄绿 → 黄 → 橙(15-20)→ 红(20+,重伤)
// 与页面既有 pass=绿 / fail=橙 / error=红 主色系保持同族,不引入新色环
const BUCKET_COLORS: Record<string, string> = {
  '0-5': '#16a34a',
  '5-10': '#84cc16',
  '10-15': '#eab308',
  '15-20': '#f97316',
  '20+': '#dc2626',
};

/** 「大于 20 是无上限」的括号显示;其它区间左闭右开写作 `[lo, hi) m/s` */
const bucketLabel = (name: string): string => {
  if (name === '20+') return '≥ 20 m/s';
  const [lo, hi] = name.split('-');
  return `[${lo}, ${hi}) m/s`;
};

interface Props {
  jobId: number | null;
}

export default function VruRelativeSpeedChart({ jobId }: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VruSpeedDistResp | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    setLoading(true);
    getVruRelativeSpeedDist(jobId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]);

  const total = data?.summary.total ?? 0;
  const hasData = total > 0;

  const chartOption: EChartsOption = useMemo(() => {
    const seriesData = (data?.buckets ?? []).map((b) => ({
      name: b.bucket,
      value: b.count,
      itemStyle: { color: BUCKET_COLORS[b.bucket] || '#8b8b8b' },
    }));
    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : '0.0';
          return `<b>${bucketLabel(params.name)}</b><br/>场景数: ${params.value}<br/>占比: ${pct}%`;
        },
      },
      legend: {
        orient: 'vertical',
        left: 'right',
        top: 'middle',
        itemGap: 8,
        icon: 'circle',
        textStyle: { fontSize: 12, color: '#4b5563' },
        // 图例显示区间语义,而不是干光光「0-5」
        formatter: (name: string) => bucketLabel(name),
      },
      series: [
        {
          name: 'VRU 相对速度分布',
          type: 'pie',
          radius: ['45%', '72%'],
          center: ['38%', '50%'],
          avoidLabelOverlap: true,
          padAngle: 2,
          itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
          label: {
            show: true,
            formatter: (params: any) => {
              if (params.value === 0) return '';
              const pct = total > 0 ? ((params.value / total) * 100).toFixed(0) : '0';
              return `${params.value} · ${pct}%`;
            },
            fontSize: 11,
            color: '#4b5563',
          },
          labelLine: { length: 8, length2: 6 },
          emphasis: {
            scaleSize: 6,
            label: { fontSize: 13, fontWeight: 600 },
          },
          data: seriesData,
        },
      ],
    };
  }, [data, total]);

  const kpiCell = (label: string, value: React.ReactNode, hint?: string) => (
    <div style={{
      flex: 1, textAlign: 'center', padding: '6px 8px',
      background: '#fafafa', borderRadius: 6, minWidth: 0,
    }}>
      <div style={{ fontSize: 11, color: '#9b9da3', marginBottom: 2 }}>
        {label}
        {hint && (
          <Tooltip title={hint}>
            <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 10, color: '#c4c6cc' }} />
          </Tooltip>
        )}
      </div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#1d1d1f', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  );

  return (
    <div style={{
      marginBottom: 16,
      padding: 16,
      background: '#fff',
      border: '1px solid #ebebeb',
      borderRadius: 8,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, gap: 8, flexWrap: 'wrap',
      }}>
        <Space size={8} align="center">
          <Text style={{ fontSize: 13, color: '#9b9da3', fontWeight: 500 }}>
            VRU 相对速度分布
          </Text>
          <Tooltip
            title={
              <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 300 }}>
                <div>取该 Job 下 VRU 责任碰撞评估器结果为 <b>Failed</b> 的场景,</div>
                <div>按发生碰撞瞬间的相对速度(m/s) 划分 5 档:</div>
                <div>[0, 5)  [5, 10)  [10, 15)  [15, 20)  [20, +∞)</div>
                <div style={{ marginTop: 4, color: '#a1a1a6' }}>
                  相对速度越大 → 碰撞越严重,颜色越暖(绿→红)
                </div>
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: '#c4c6cc', fontSize: 12 }} />
          </Tooltip>
          <Tag color="volcano">VRU 责任碰撞 · Failed</Tag>
        </Space>
      </div>

      <Spin spinning={loading}>
        {!hasData && !loading ? (
          <Empty
            description="该 Job 无 VRU 责任碰撞失败场景"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '20px 0' }}
          />
        ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', minHeight: 240 }}>
            {/* 饼图占大头,自适应父宽 */}
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <ReactECharts
                option={chartOption}
                style={{ height: 240, width: '100%' }}
                opts={{ renderer: 'canvas' }}
                notMerge
              />
            </div>
            {/* 侧栏 3 格 KPI:样本数 / 均值 / 最大值,纵向排列避免挤 chart */}
            <div style={{
              width: 140, flexShrink: 0,
              display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center',
            }}>
              {kpiCell('失败场景数', total)}
              {kpiCell(
                '平均相对速度',
                data?.summary.avgSpeed != null
                  ? `${data.summary.avgSpeed.toFixed(2)} m/s`
                  : '—',
                'Failed 场景的 relative_speed 算术平均',
              )}
              {kpiCell(
                '最大相对速度',
                data?.summary.maxSpeed != null
                  ? `${data.summary.maxSpeed.toFixed(2)} m/s`
                  : '—',
              )}
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
}
